import React, { useEffect, useState, useCallback } from 'react';
import { Autocomplete, TextField, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import {
  getCurrentHostingId,
  setCurrentHostingId,
  clearCurrentHostingId
} from '../utils/hostingStorage';

const HostingSwitcher = () => {
  const { user } = useAuth();
  const { t } = useTranslation('menu');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [hostings, setHostings] = useState([]);
  const [value, setValue] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const role = user?.role;
  const isHoster = role === 'hoster' || role === 'manager';

  useEffect(() => {
    if (!isHoster) {
      setHostings([]);
      setValue(null);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    api.get('/me/hostings')
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data?.hostings) ? resp.data.hostings : [];
        setHostings(list);
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('hosting_id');
        const fromStorage = getCurrentHostingId();
        const initial =
          list.find((h) => h.id === fromUrl) ||
          list.find((h) => h.id === fromStorage) ||
          null;
        setValue(initial);
        if (initial) {
          setCurrentHostingId(initial.id);
        } else if (fromStorage && !list.find((h) => h.id === fromStorage)) {
          clearCurrentHostingId();
        }
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setHostings([]);
        setValue(null);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isHoster, user?.id]);

  // Подтягиваем выбор из URL (внешний линк / navigate). value?.id в depths не кладём:
  // setValue(null) при очистке успевает раньше обновления useLocation, и эффект бы
  // считывал ещё старый search и возвращал значение обратно (двойной клик на крестик).
  useEffect(() => {
    if (!loaded || hostings.length < 2) return;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('hosting_id');
    if (!fromUrl) return;
    const matched = hostings.find((h) => h.id === fromUrl);
    if (!matched) return;
    setValue((prev) => (prev?.id === matched.id ? prev : matched));
    setCurrentHostingId(matched.id);
  }, [location.search, hostings, loaded]);

  const handleChange = useCallback(
    (_, newValue) => {
      setValue(newValue);
      const params = new URLSearchParams(location.search);
      if (newValue) {
        params.set('hosting_id', newValue.id);
        setCurrentHostingId(newValue.id);
      } else {
        params.delete('hosting_id');
        clearCurrentHostingId();
      }
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  if (!isHoster || hostings.length < 2) return null;

  return (
    <Autocomplete
      options={hostings}
      value={value}
      onChange={handleChange}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      size="small"
      sx={{
        width: isMobile ? 130 : 220,
        marginRight: 1,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255,255,255,0.08)',
          color: 'white',
          paddingTop: '2px',
          paddingBottom: '2px',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
          '&.Mui-focused fieldset': { borderColor: 'white' }
        },
        '& .MuiInputBase-input': { color: 'white', fontSize: isMobile ? '0.8rem' : '0.9rem' },
        '& .MuiSvgIcon-root': { color: 'white' }
      }}
      // дефолтный clearIndicator очищает только со второго клика (первым закрывает popper)
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('hostingSwitcherPlaceholder')}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {value && (
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => { e.stopPropagation(); handleChange(e, null); }}
                    sx={{ color: 'white', padding: '2px', marginRight: '-4px' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default HostingSwitcher;
